<script lang="ts">
  import { questionnaire } from "../stores/questionnaire";
  import { t, translationExists } from "../stores/translations";
  import BmiSelector from "./selectors/BmiSelector.svelte";
  import RadioSelector from "./selectors/RadioSelector.svelte";
  import SliderSelector from "./selectors/SliderSelector.svelte";
  import ThumbnailSelector from "./selectors/ThumbnailSelector.svelte";
  import InfoButton from "../assets/icons/info-button.svg";
  import Modal from "./shared/Modal.svelte";
  import { fade } from "svelte/transition";
  import InfoPrompt from "./InfoPrompt.svelte";
  import { videoData } from "../data/videoData";
  import { locale } from "../stores/locale";
  import Icon from "./shared/Icon.svelte";
  import { googleTagManagerStore } from "../stores/googleTagManager";
  import Validation from "./Validation.svelte";
  import { getNewMattressReason } from "../helpers/helpers";

  const mapping = {
    "new-mattress-reason": {
      component: RadioSelector,
    },
    "length-weight": {
      component: BmiSelector,
    },
    "length-weight-partner": {
      component: BmiSelector,
    },
    "body-type": {
      component: ThumbnailSelector,
      properties: {
        video: videoData,
      },
    },
    "body-type-partner": {
      component: ThumbnailSelector,
      properties: {
        video: videoData,
      },
    },
    "mattress-type": {
      component: ThumbnailSelector,
      properties: {
        styling: "height: 55px",
      },
    },
    "budget-one-person": {
      component: SliderSelector,
    },
    "budget-two-persons": {
      component: SliderSelector,
    },
    "advice-partner": {
      component: RadioSelector,
    },
  };

  let modalHeader: string;
  let modalContent: string;
  let modalOpened: boolean = false;

  function setAnswer(question, answer): void {
    questionnaire.setAnswer(question, answer);

    if ($googleTagManagerStore.gtmActive) {
      googleTagManagerStore.selectAnswer(
        question.slug,
        getGtmAnswer(question.slug, answer.slug)
      );
    }
  }

  function getGtmAnswer(question: string, answer: string): string {
    if (question.includes("length-weight")) return "n/a";

    if (question !== "new-mattress-reason") return answer;

    return getNewMattressReason(answer);
  }

  function setModalContentAndShow(questionSlug: string): void {
    modalHeader = $t(`questions.${questionSlug}.title`);
    modalContent = $t(`questions.${questionSlug}.information.content`);

    modalOpened = true;
  }

  /**
   * Scroll newly rendered element into view.
   * @param node
   */
  function scrollIntoView(node: HTMLElement): void {
    if (
      +node.getAttribute("data-question-index") > 3 ||
      node.getAttribute("data-question-info-index")
    ) {
      node.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }
</script>

{#each $questionnaire.activeQuestions as question, index}
  <section
    class="question"
    transition:fade
    use:scrollIntoView
    data-question-index={index}>
    <Validation slug={question.slug} />
    <div class="header">
      <h1>{$t(`questions.${question.slug}.title`)}</h1>

      {#if $translationExists(`questions.${question.slug}.information.content`)}
        <Icon
          src={InfoButton}
          alt="Information button icon"
          on:click={() => setModalContentAndShow(question.slug)} />
      {/if}
    </div>

    <svelte:component
      this={mapping[question.slug].component}
      properties={mapping[question.slug].properties}
      selectedId={question.selectedAnswer?.id}
      on:select={(event) => setAnswer(question, event.detail)}
      {question} />
  </section>

  {#if question.additionalInfo}
    <section
      transition:fade
      use:scrollIntoView
      class="info-prompt"
      data-question-info-index={index}>
      <InfoPrompt
        answerSlug={question.additionalInfo.slug}
        url={question.additionalInfo.urls[$locale]}
        questionSlug={question.slug} />
    </section>
  {/if}
{/each}

<Modal bind:opened={modalOpened} header={modalHeader}>
  <p class="modal-content">
    {@html modalContent}
  </p>
</Modal>

<style lang="scss">
  @import "src/lib/styles/variables";
  @import "src/lib/styles/mixins";

  .modal-content {
    font-family: "Roboto";
    color: $color-text;
    font-size: $font-size-medium;
    line-height: 1.5rem;
    margin: 0.5rem 0;
  }

  section {
    padding: 1.5rem;
    margin-bottom: 1.5rem;

    &.question {
      background-color: $color-white;
      border-radius: 8px;
    }

    &.info-prompt {
      padding: 0 1.5rem;
    }

    & > .header {
      display: flex;

      & > h1 {
        margin: 0 0 1rem;
        font-size: $font-size-large;
        line-height: 1.5rem;
        color: $color-primary;
        font-family: "Roboto";
        font-weight: 700;
        margin-right: 0.75rem;
      }
    }
  }

  @include desktop {
    section {
      padding: 2.5rem;
      & > .header {
        & > h1 {
          margin-bottom: 1.25rem;
          font-size: $font-size-extra-large;
        }
      }

      &.info-prompt {
        padding: 0;
      }
    }
  }
</style>
